// A GTFS city's page: the shell (@tol/ui) fed with the city's network and
// live trips, plus service alerts on the line list when the city has them,
// all kept by its client (client.ts). A city is the config below and one
// call: createGtfsPage(config).

import type { LineId, Network } from '@tol/core/types';
import type { RosterLine } from '@tol/ui/roster';
import { createShell } from '@tol/ui/shell';
import type { LineGroup, Noun, RosterStatus, Shell, ShellOptions, StatusKind } from '@tol/ui/shell';
import type { CardTrain, TripRow } from '@tol/ui/card';
import { el, esc } from '@tol/ui/dom';
import { createGtfsClient } from './client.ts';
import type { GtfsClient } from './client.ts';
import { alertKind } from './closures.ts';
import { lineHours } from './hours.ts';
import { gtfsTrainNote, gtfsTripRows } from './trip-rows.ts';
import type { AlertKind, LineAlert } from './types.ts';

// A line as the page draws it: the shell's PageLine plus which of the city's
// systems it belongs to, and the bullet drawn on its trains zoomed in.
export interface GtfsPageLine extends RosterLine {
  readonly system: string;
  readonly bullet?: string;
}

// A line's most serious active alert (GET /alerts): types.ts.
export type { LineAlert } from './types.ts';

export interface GtfsPageConfig {
  readonly hooks: string;
  readonly city: string;
  readonly lines: readonly GtfsPageLine[];
  // The systems in the order the line list groups them, with their names.
  readonly systems: readonly {
    readonly id: string;
    readonly name: string;
    readonly global?: boolean;
    readonly modeName?: string;
  }[];
  readonly accuracyGroups?: readonly LineGroup[];
  readonly view: ShellOptions['view'];
  readonly credits: readonly string[];
  // Which side trains keep to: one for the city, or per line (Paris: the
  // Métro right, the SNCF's railways left).
  readonly keepRight?: boolean | ((lineId: string) => boolean);
  // The zone used by network.json's GTFS service calendar. Defaults to the
  // display zone for ordinary single-zone city pages.
  readonly serviceTimeZone?: string;
  readonly timeZone: string;
  readonly locale: string;
  readonly dataName: string;
  // What the summary counts ("59 trams running"); default trains.
  readonly noun?: Noun;
  // Poll /trains this often (ms); the server refreshes its feeds behind it.
  readonly pollMs?: number;
  // The city serves /alerts.
  readonly alerts?: boolean;
  // A standing note on a line's row, under any alert (Budapest's Sikló: no
  // live data).
  readonly lineNotes?: Readonly<Record<LineId, string>>;
  // Data older than this (s) turns the live dot stale; default 120. More for a
  // city whose server polls its feeds slowly.
  readonly staleAfterS?: number;
}

const RETRY_MS = 3000;
const NETWORK_RETRY_CAP_MS = 30000;

// A 409: the server has another deploy's files than this page's (it was
// deployed between the page loading and asking), so asking again never
// works. The page is loaded again, once a minute at most (a server that
// keeps answering 409 doesn't loop the page).
const RELOADED_KEY = 'tol:reloaded-for-deploy';
function reloadForDeploy(error: unknown): boolean {
  const status = typeof error === 'object' && error !== null && 'status' in error ? error.status : 0;
  if (status !== 409) return false;
  try {
    const last = Number(sessionStorage.getItem(RELOADED_KEY) ?? 0);
    if (Date.now() - last < 60_000) return false;
    sessionStorage.setItem(RELOADED_KEY, String(Date.now()));
  } catch {
    // No storage (a private window's quota): no loop guard, so no reload.
    return false;
  }
  location.reload();
  return true;
}

// The page needs its network: asked for until it comes, with bounded
// backoff between tries.
async function loadNetwork(client: GtfsClient, attempt = 1): Promise<Network> {
  try {
    return await client.load();
  } catch (error) {
    if (reloadForDeploy(error)) {
      el('#loader-text').textContent = 'The site was just updated. Reloading…';
      return new Promise<Network>(() => {
        // The reload replaces the page.
      });
    }
    const delay = Math.min(RETRY_MS * 2 ** (attempt - 1), NETWORK_RETRY_CAP_MS);
    el('#loader-text').textContent =
      'Network unavailable. Retrying shortly. Check connection or reload if this persists.';
    console.warn('Network load failed, retrying', error);
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        resolve();
      }, delay);
    });
    return loadNetwork(client, attempt + 1);
  }
}

export { alertKind };

// The line list's status for an alert: a change is planned works, or a notice.
export const statusKind = (kind: AlertKind, planned: boolean): StatusKind =>
  kind === 'changed' ? (planned ? 'planned' : 'info') : kind;

// A line's row: its alert, else what the timetable says when it has no
// trains, else a good service where the alerts speak for it; undefined for
// none of those.
export function lineStatus(
  alert: LineAlert | undefined,
  options: {
    readonly covered: boolean;
    // No trains now, and the timetable has none till `next` (ms; null: not
    // today or tomorrow). Undefined: trains, or it doesn't say.
    readonly off?: { readonly next: number | null };
    readonly clock: (ms: number) => string;
  },
): RosterStatus | undefined {
  if (alert) {
    const kind = alertKind(alert);
    const items = alert.all ?? [
      {
        type: alert.type,
        text: alert.text,
        ...(alert.detail === undefined ? {} : { detail: alert.detail }),
        kind,
        planned: alert.planned,
      },
    ];
    // The alert's type (planned work without the "Planned - "), red when it
    // isn't planned, and the operator's headline on hover and on a tap.
    return {
      text: alert.type.replace(/^Planned - /u, ''),
      bad: !alert.planned,
      title: alert.text + (alert.count > 1 ? `\n\n+${alert.count - 1} more` : ''),
      kind: statusKind(kind, alert.planned),
      detail: items.map((i) => {
        const title = i.type.replace(/^Planned - /u, '');
        return i.detail === undefined ? { title, text: i.text } : { title, text: i.text, more: i.detail };
      }),
    };
  }
  if (options.off)
    return {
      text: options.off.next === null ? 'No service today' : `No service until ${options.clock(options.off.next)}`,
      bad: false,
      title: '',
      kind: 'info',
    };
  return options.covered ? { text: 'Good service', bad: false, title: '', kind: 'good' } : undefined;
}

// Why a line has no trains: its alert closes it, the timetable has none now,
// or it should have some and the feed has none; undefined: the page can't
// tell (no timetable hours for now).
export function idleReason(
  alert: LineAlert | undefined,
  hours: { readonly running: boolean } | null,
): string | undefined {
  if (alert && alertKind(alert) === 'closed') return 'closed';
  if (!hours) return undefined;
  return hours.running ? 'no live data' : 'not running now';
}

export async function createGtfsPage(config: GtfsPageConfig): Promise<Shell> {
  const { pollMs = 15000, staleAfterS = 120 } = config;
  const groups: LineGroup[] = config.systems.map((s) => ({
    ...s,
    id: s.id,
    name: s.name,
    lines: config.lines.filter((l) => l.system === s.id).map((l) => l.id),
  }));

  // The city's data (client.ts), once the shell it feeds is made.
  let gtfs: GtfsClient | null = null;
  const tripRows = (t: CardTrain, net: Network): readonly TripRow[] =>
    gtfsTripRows(t, net, gtfs?.trip(t.pred.key), gtfs?.file()?.shapes ?? {});

  const clockFmt = new Intl.DateTimeFormat(config.locale, {
    timeZone: config.timeZone,
    hour: '2-digit',
    minute: '2-digit',
  });
  // The timetable's word on a line now, when it has no trains.
  // The shell's train counts, once it's made.
  let counted: { readonly counts: Partial<Record<LineId, number>> } | null = null;
  const hoursOf = (id: LineId): ReturnType<typeof lineHours> =>
    (counted?.counts[id] ?? 0) > 0
      ? null
      : lineHours(gtfs?.file()?.hours, id, Date.now(), config.serviceTimeZone ?? config.timeZone);

  const status = (id: LineId): RosterStatus | undefined => {
    const alert = gtfs?.alert(id);
    const note = config.lineNotes?.[id];
    // A line with a note of its own (Budapest's Sikló) and no alert: the note.
    if (!alert && note !== undefined) return { text: note, bad: false, title: '' };
    const h = hoursOf(id);
    return lineStatus(alert, {
      covered: gtfs?.covered(id) ?? false,
      ...(h && !h.running ? { off: { next: h.next } } : {}),
      clock: (ms) => clockFmt.format(ms),
    });
  };
  const idle = (id: LineId): string | undefined => idleReason(gtfs?.alert(id), hoursOf(id));

  const shell = await createShell({
    hooks: config.hooks,
    city: config.city,
    lines: config.lines,
    groups,
    ...(config.accuracyGroups ? { accuracyGroups: config.accuracyGroups } : {}),
    view: config.view,
    credits: config.credits,
    ...(config.keepRight === undefined ? {} : { keepRight: config.keepRight }),
    timeZone: config.timeZone,
    locale: config.locale,
    dataName: config.dataName,
    ...(config.noun ? { noun: config.noun } : {}),
    staleAfterS,
    // Until an answer with every feed in.
    loading: () => gtfs?.loading() ?? true,
    card: {
      tripRows,
      trainNote: (t) => gtfsTrainNote(t, gtfs?.trip(t.pred.key)),
    },
    status,
    idle,
    onHidden: () => {
      gtfs?.stop();
    },
    onVisible: () => {
      void gtfs?.start();
    },
  });
  counted = shell.state;
  const client = createGtfsClient({
    name: config.hooks,
    base: '',
    version: shell.version,
    api: shell.client,
    store: shell.live,
    key: 0,
    lines: config.lines.map((l) => l.id),
    now: shell.now,
    pollMs,
    alerts: config.alerts === true,
    major: false,
    isGeometry: shell.isGeometryFile,
    onUpdate: shell.updateArrivalUI,
    onProblem: (down) => {
      if (down === null) shell.showToast('Train updates are unavailable. Retrying shortly.');
      else if (down.length > 0) shell.showToast(`Some lines have no live data right now (${esc(down.join(', '))}).`);
      else shell.hideToast();
    },
    onNetwork: (net) => {
      shell.setNetwork(net);
    },
    onRebuilt: () => {
      location.reload();
    },
  });
  gtfs = client;

  el('#loader-text').textContent = 'Drawing the network';
  shell.setNetwork(await loadNetwork(client));
  await client.start();
  shell.ready();
  return shell;
}
